/**
 * lib/notifications.ts
 * ------------------------------------------------------------------
 * P3 routing: when a flag is raised on a student, work out which
 * editor(s) should be notified — the editor(s) whose editorForms
 * covers that student's form level. Plus a builder for the
 * notification document.
 *
 * Deliberate divergence from Al-Nur (noted in CHANGES): Al-Nur
 * creates notifications via a deployed Cloud Function. We write
 * the notification client-side in the same action as the flag, so
 * there is NO deploy dependency (consistent with the email/AI
 * deferral). Same data outcome; the writer is authenticated and
 * the routing is deterministic.
 * ------------------------------------------------------------------
 */
import type { AppUser, CommentFlag, AppNotification } from '@/types';
import { getFormLevel } from '@/lib/grading';

/**
 * Editors who should receive a flag for this class. An editor is a
 * recipient if their role is 'editor' and their editorForms
 * includes the form level of the class. Admin/headteacher are NOT
 * auto-notified (they review via the panel, not the inbox) to
 * avoid noise — matches "flags route to the form's editor".
 *
 * Fallback: if NO editor is assigned to that form, every active
 * editor is notified, so a flag is never silently unrouted (the
 * last-line-of-defence principle). The caller can surface this.
 */
export function editorsForClass(
  users: AppUser[],
  className: string,
): { recipients: AppUser[]; unrouted: boolean } {
  const form = getFormLevel(className);
  const editors = users.filter(
    (u) => u.role === 'editor' && u.isActive !== false,
  );
  if (form === null) {
    return { recipients: editors, unrouted: true };
  }
  const assigned = editors.filter((u) =>
    (u.editorForms ?? []).includes(form),
  );
  if (assigned.length > 0) {
    return { recipients: assigned, unrouted: false };
  }
  // no editor owns this form — notify all editors, flag as unrouted
  return { recipients: editors, unrouted: true };
}

/** Build a notification document for a raised flag. */
export function buildFlagNotification(
  flag: Pick<
    CommentFlag,
    | 'studentName'
    | 'className'
    | 'reasonLabel'
    | 'reason'
    | 'target'
    | 'subjectName'
    | 'classId'
    | 'studentId'
  >,
  recipientId: string,
  flagId: string,
): Omit<AppNotification, 'id'> & { flagId: string } {
  const what =
    flag.target === 'subjectComment' && flag.subjectName
      ? `${flag.subjectName} comment`
      : flag.target === 'mark' && flag.subjectName
        ? `${flag.subjectName} mark`
        : flag.target === 'classTeacherComment'
          ? 'class-teacher comment'
          : flag.target === 'hodComment'
            ? 'HOY comment'
            : 'comment';
  return {
    recipientId,
    type: 'flag',
    title: 'A comment was flagged for review',
    message: `${flag.studentName} (${flag.className ?? 'class'}) — ${what} flagged: ${
      flag.reasonLabel || flag.reason
    }`,
    // classId + studentId let the inbox deep-link straight to the
    // flagged student in the Comment Review editor (P4).
    details: {
      flagId,
      classId: flag.classId,
      studentId: flag.studentId,
    },
    read: false,
    createdAt: new Date().toISOString(),
    flagId,
  };
}
